// frontend/src/slices/cartSlice.js - Clean version without discounts

import { createSlice } from "@reduxjs/toolkit";

// Unique key for cart items: fixed-price products need product+selectedWeight (1kg and 500g are separate)
export const getCartItemKey = (item) => {
    if (item.priceType === 'fixed' && item.selectedWeight) {
        return `${item.product}_${item.selectedWeight}`;
    }
    return String(item.product || '');
};

const cartSlice = createSlice({
    name: 'cart',
    initialState: {
        items: localStorage.getItem('cartItems')
            ? JSON.parse(localStorage.getItem('cartItems'))
            : [],
        loading: false,
        shippingInfo: localStorage.getItem('shippingInfo')
            ? JSON.parse(localStorage.getItem('shippingInfo'))
            : {}
    },
    reducers: {
        addCartItemRequest(state) {
            state.loading = true;
        },
        // Clean version without discount calculations
        // For fixed-price products (e.g. 500g vs 1kg), treat as separate line items - add, don't replace
        addCartItemSuccess(state, action) {
            const item = action.payload;
            const itemKey = getCartItemKey(item);
            const existingItem = state.items.find(i => getCartItemKey(i) === itemKey);
            
            let basePrice = item.price;
            if (item.priceType === 'perDozen') {
                basePrice = item.dozenPrice || item.price;
            } else if (item.priceType === 'perPack') {
                basePrice = item.packPrice || item.price;
            }
            
            if (existingItem) {
                const newQty = existingItem.quantity + item.quantity;
                state.items = state.items.map(i =>
                    getCartItemKey(i) === itemKey ? {
                        ...item,
                        quantity: newQty,
                        price: basePrice,
                        dozenPrice: item.dozenPrice || basePrice,
                        packPrice: item.packPrice || basePrice,
                        finalPrice: basePrice * newQty,
                        category: item.category
                    } : i
                );
            } else {
                state.items = [...state.items, {
                    ...item,
                    price: basePrice,
                    dozenPrice: item.dozenPrice || basePrice,
                    packPrice: item.packPrice || basePrice,
                    finalPrice: basePrice * item.quantity,
                    category: item.category
                }];
            }
            
            state.loading = false;
            localStorage.setItem('cartItems', JSON.stringify(state.items));
        },
        // Clean quantity increase without discount calculations (payload = cartItemKey)
        increaseCartItemQty(state, action) {
            const targetKey = action.payload;
            state.items = state.items.map(item => {
                if (getCartItemKey(item) === targetKey) {
                    const newQty = item.quantity + 1;
                    let basePrice = item.price;
                    
                    if (item.priceType === 'perDozen') {
                        basePrice = item.dozenPrice || item.price;
                    } else if (item.priceType === 'perPack') {
                        basePrice = item.packPrice || item.price;
                    }
                    
                    return {
                        ...item,
                        quantity: newQty,
                        finalPrice: basePrice * newQty
                    };
                }
                return item;
            });
            localStorage.setItem('cartItems', JSON.stringify(state.items));
        },
        decreaseCartItemQty(state, action) {
            const targetKey = action.payload;
            state.items = state.items.map(item => {
                if (getCartItemKey(item) === targetKey && item.quantity > 1) {
                    const newQty = item.quantity - 1;
                    let basePrice = item.price;
                    
                    if (item.priceType === 'perDozen') {
                        basePrice = item.dozenPrice || item.price;
                    } else if (item.priceType === 'perPack') {
                        basePrice = item.packPrice || item.price;
                    }
                    
                    return {
                        ...item,
                        quantity: newQty,
                        finalPrice: basePrice * newQty
                    };
                }
                return item;
            });
            localStorage.setItem('cartItems', JSON.stringify(state.items));
        },
        removeItemFromCart(state, action) {
            const targetKey = action.payload;
            state.items = state.items.filter(item => getCartItemKey(item) !== targetKey);
            localStorage.setItem('cartItems', JSON.stringify(state.items));
        },
        saveShippingInfo(state, action) {
            state.shippingInfo = action.payload;
            localStorage.setItem('shippingInfo', JSON.stringify(action.payload));
        },
        clearCart(state) {
            state.items = [];
            localStorage.removeItem('cartItems');
        },
        // Clean update without discount calculations
        updateCartItems(state, action) {
            state.items = action.payload.map(item => {
                let basePrice = item.price;
                
                if (item.priceType === 'perDozen') {
                    basePrice = item.dozenPrice || item.price;
                } else if (item.priceType === 'perPack') {
                    basePrice = item.packPrice || item.price;
                }
                
                return {
                    ...item,
                    finalPrice: basePrice * item.quantity
                };
            });
            localStorage.setItem('cartItems', JSON.stringify(state.items));
        }
    }
});

export const {
    addCartItemRequest,
    addCartItemSuccess,
    increaseCartItemQty,
    decreaseCartItemQty,
    removeItemFromCart,
    saveShippingInfo,
    clearCart,
    updateCartItems
} = cartSlice.actions;

export default cartSlice.reducer;